/**
 * WelcomeNudge — One-time modal shown on first login to point new users
 * toward the Quick-Start Guide accessible from their profile menu.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isGuideDismissed, dismissGuide } from '../lib/storage';
import { HelpCircle, X } from 'lucide-react';

export default function WelcomeNudge() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isGuideDismissed()) {
      setVisible(true);
    }
  }, []);

  function handleDismiss() {
    dismissGuide();
    setVisible(false);
  }

  function handleGoToGuide() {
    dismissGuide();
    setVisible(false);
    navigate('/guide');
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div className="relative bg-surface-raised border border-edge-default rounded-2xl shadow-2xl max-w-sm w-full p-6">
        {/* Close button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-content-tertiary
                     hover:bg-surface-sunken hover:text-content-primary
                     transition-colors duration-150 cursor-pointer border-none bg-transparent"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-action-primary/15 flex items-center justify-center">
            <HelpCircle className="h-6 w-6 text-action-primary" />
          </div>
        </div>

        {/* Content */}
        <h2 className="text-lg font-bold text-content-primary text-center mb-2">Welcome to ShiftHappens!</h2>
        <p className="text-sm text-content-secondary text-center leading-relaxed mb-2">
          Need help getting started? There's a <strong>Quick-Start Guide</strong> available anytime from your profile
          menu — look for <strong>"Help / Guide"</strong> in the sidebar or the menu icon.
        </p>
        <p className="text-xs text-content-tertiary text-center leading-relaxed mb-5">
          It covers installation, clocking in and out, viewing your hours, and more.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleGoToGuide}
            className="w-full py-2.5 px-4 text-sm font-semibold rounded-lg
                       bg-action-primary text-content-on-primary
                       hover:brightness-110 transition-all duration-150
                       cursor-pointer border-none"
          >
            Open Quick-Start Guide
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-full py-2.5 px-4 text-sm font-medium rounded-lg
                       bg-transparent text-content-secondary border border-edge-default
                       hover:bg-surface-sunken transition-colors duration-150
                       cursor-pointer"
          >
            Got it, I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
